class InfiniteScrollGallery extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('.infinite-scroll-gallery__grid');
    this.sentinelBottom = this.querySelector('.infinite-scroll-gallery__sentinel');
    this.sentinelTop = this.querySelector('.infinite-scroll-gallery__sentinel-top');

    this.originalCards = Array.from(
      this.grid.querySelectorAll(':scope > product-card')
    );

    if (this.originalCards.length === 0) return;

    this.totalCards = this.originalCards.length;
    this._lastAppend = 0;

    // Shuffle the initial server-rendered order in place
    this._shuffleDOM(this.originalCards);

    // Pre-fill below the fold before handing off to the observer
    const minCards = 5 * 4; // 5 cols × 4 rows
    while (this.totalCards < minCards) {
      this._appendBatch();
    }

    // Bottom observer — append when approaching the end
    this._observerBottom = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) this._appendBatch();
      },
      { rootMargin: '3000px 0px' }
    );
    this._observerBottom.observe(this.sentinelBottom);

    // Scroll fallback — catches fast scrolls that outrun the observer.
    // Checks both directions: append at bottom, prepend at top.
    this._scrollHandler = () => {
      const scrollY = window.scrollY;
      const remaining = document.documentElement.scrollHeight - scrollY - window.innerHeight;
      if (remaining < 1500) this._appendBatch();
      if (scrollY < 1500 && this._topObserverActive) this._prependBatch();
    };
    window.addEventListener('scroll', this._scrollHandler, { passive: true });

    // Top observer — only activate after the first scroll so it doesn't
    // fire immediately on page load when the sentinel is already in view
    window.addEventListener('scroll', this._activateTopObserver.bind(this), {
      passive: true,
      once: true,
    });
  }

  disconnectedCallback() {
    if (this._observerBottom) this._observerBottom.disconnect();
    if (this._observerTop) this._observerTop.disconnect();
    if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler);
  }

  _activateTopObserver() {
    this._topObserverActive = true;
    this._observerTop = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) this._prependBatch();
      },
      { rootMargin: '1500px 0px 0px 0px' }
    );
    this._observerTop.observe(this.sentinelTop);
  }

  // Fisher-Yates — moves existing DOM nodes in shuffled order
  _shuffleDOM(cards) {
    this._shuffleArray(cards);
    const frag = document.createDocumentFragment();
    for (const card of cards) frag.appendChild(card);
    this.grid.appendChild(frag);
  }

  // Fisher-Yates in-place shuffle, returns the same array
  _shuffleArray(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // Build a cloned batch with eager image loading so cached images paint instantly
  _cloneBatch() {
    const order = this._shuffleArray([...this.originalCards]);
    const frag = document.createDocumentFragment();
    for (const card of order) {
      const clone = card.cloneNode(true);
      clone.querySelectorAll('img').forEach((img) => { img.loading = 'eager'; });
      frag.appendChild(clone);
    }
    return frag;
  }

  _appendBatch() {
    // Timestamp throttle — allows re-entry every 200 ms so aggressive
    // scrolls can trigger multiple appends without a frame-long dead zone
    const now = Date.now();
    if (now - this._lastAppend < 200) return;
    this._lastAppend = now;

    // Two batches per trigger doubles the content buffer added at once
    this.grid.appendChild(this._cloneBatch());
    this.grid.appendChild(this._cloneBatch());
    this.totalCards += this.originalCards.length * 2;
  }

  _prependBatch() {
    if (this._prependBusy) return;
    this._prependBusy = true;

    // Snapshot anchor position before mutating the DOM
    const anchor = this.grid.firstElementChild;
    const anchorTop = anchor ? anchor.getBoundingClientRect().top : 0;

    this.grid.prepend(this._cloneBatch());
    this.totalCards += this.originalCards.length;

    // Correct scroll position to prevent the viewport from jumping.
    // If the browser already scroll-anchored, delta will be 0 — a safe no-op.
    if (anchor) {
      const delta = anchor.getBoundingClientRect().top - anchorTop;
      if (delta) window.scrollBy({ top: delta, behavior: 'instant' });
    }

    // Cooldown so a single slow scroll near the top doesn't stack prepends
    setTimeout(() => { this._prependBusy = false; }, 400);
  }
}

customElements.define('infinite-scroll-gallery', InfiniteScrollGallery);
